import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Resource, TenantRoleDto } from "@exibra/shared";
import { useCreateTenantRole, useUpdateTenantRole } from "@/api/tenant.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/label/label";
import { typedZodResolver } from "@/lib/typed-zod-resolver";

interface RoleFormProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Callback when dialog closes */
    onClose: () => void;
    /** Role to edit (optional, if not provided, creates new role) */
    role?: TenantRoleDto;
}

// Schema factory function to use translations
const createRoleFormSchema = (t: (key: string) => string) =>
    z.object({
        name: z.string().min(1, { message: t("users.roles.form.name.required") }),
    });

type RoleFormData = {
    name: string;
};

type SimplePermissions = {
    administrator: boolean;
    changeScreenSettings: boolean;
    editTemplates: boolean;
};

// Helper function to convert simple permissions to detailed permissions
const convertToDetailedPermissions = (simplePerms: SimplePermissions) => {
    const permissions = [
        // Screen permissions
        {
            module: Resource.Screen,
            read: simplePerms.administrator || simplePerms.changeScreenSettings,
            create: simplePerms.administrator || simplePerms.changeScreenSettings,
            update: simplePerms.administrator || simplePerms.changeScreenSettings,
            delete: simplePerms.administrator,
        },
        // Template permissions
        {
            module: Resource.Template,
            read:
                simplePerms.administrator ||
                simplePerms.changeScreenSettings ||
                simplePerms.editTemplates,
            create: simplePerms.administrator || simplePerms.editTemplates,
            update: simplePerms.administrator || simplePerms.editTemplates,
            delete: simplePerms.administrator,
        },
        // User permissions
        {
            module: Resource.WebsiteApplication,
            read: simplePerms.administrator || simplePerms.changeScreenSettings || simplePerms.editTemplates,
            create: simplePerms.administrator || simplePerms.editTemplates,
            update: simplePerms.administrator || simplePerms.editTemplates,
            delete: simplePerms.administrator,
        },
        {
            module: Resource.User,
            read: simplePerms.administrator,
            create: simplePerms.administrator,
            update: simplePerms.administrator,
            delete: simplePerms.administrator,
        },
        // Tenant permissions
        {
            module: Resource.Tenant,
            read: simplePerms.administrator,
            create: simplePerms.administrator,
            update: simplePerms.administrator,
            delete: simplePerms.administrator,
        },
        // Billing permissions
        {
            module: Resource.Billing,
            read: simplePerms.administrator,
            create: simplePerms.administrator,
            update: simplePerms.administrator,
            delete: simplePerms.administrator,
        },
    ];

    return permissions;
};

// Helper function to convert detailed permissions to simple permissions
const convertToSimplePermissions = (
    permissions: TenantRoleDto["permissions"],
): SimplePermissions => {
    const hasUserPermissions = permissions.some(
        (p) => p.module === Resource.User && (p.read || p.create || p.update || p.delete),
    );
    const hasBillingPermissions = permissions.some(
        (p) => p.module === Resource.Billing && (p.read || p.create || p.update || p.delete),
    );
    const hasDeletePermissions = permissions.some((p) => p.delete);

    const administrator = hasUserPermissions && hasBillingPermissions && hasDeletePermissions;

    const canWriteScreens = permissions.some(
        (p) => p.module === Resource.Screen && (p.create || p.update),
    );
    const changeScreenSettings = canWriteScreens && !administrator;

    const canWriteTemplates = permissions.some(
        (p) => p.module === Resource.Template && (p.create || p.update),
    );
    const editTemplates = canWriteTemplates && !administrator && !changeScreenSettings;

    return {
        administrator,
        changeScreenSettings,
        editTemplates,
    };
};

export const RoleForm: React.FC<RoleFormProps> = ({ open, onClose, role }) => {
    const { t } = useTranslation();
    const selectedTenant = useTenantStore((state) => state.selectedTenant);
    const { mutate: createRole, isPending: isCreating } = useCreateTenantRole();
    const { mutate: updateRole, isPending: isUpdating } = useUpdateTenantRole();

    const isEditing = Boolean(role);
    const isSubmitting = isCreating || isUpdating;

    // State for simple permissions
    const [simplePermissions, setSimplePermissions] = useState<SimplePermissions>({
        administrator: false,
        changeScreenSettings: false,
        editTemplates: false,
    });

    const roleFormSchema = createRoleFormSchema(t);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, touchedFields },
    } = useForm<RoleFormData>({
        resolver: typedZodResolver<RoleFormData>(roleFormSchema),
        defaultValues: {
            name: "",
        },
        mode: "onChange",
    });

    // Initialize permissions when dialog opens or role changes
    useEffect(() => {
        if (open) {
            if (role) {
                setSimplePermissions(convertToSimplePermissions(role.permissions));
                reset({
                    name: role.name,
                });
            } else {
                setSimplePermissions({
                    administrator: false,
                    changeScreenSettings: false,
                    editTemplates: false,
                });
                reset({
                    name: "",
                });
            }
        }
    }, [role, open, reset]);

    const onSubmit = (data: RoleFormData) => {
        if (!selectedTenant) return;

        // Transform simple permissions to detailed permissions
        const permissionsArray = convertToDetailedPermissions(simplePermissions);

        if (isEditing && role) {
            updateRole({
                tenantId: selectedTenant.id,
                roleId: role.id,
                roleData: { name: data.name, permissions: permissionsArray },
            });
        } else {
            createRole({
                tenantId: selectedTenant.id,
                roleData: { name: data.name, permissions: permissionsArray },
            });
        }

        onClose();
    };

    const handlePermissionChange = (permission: keyof SimplePermissions, checked: boolean) => {
        setSimplePermissions((prev) => {
            const newPerms = { ...prev };

            if (permission === "administrator") {
                // If administrator is enabled, enable all permissions
                // If administrator is disabled, disable all permissions
                newPerms.administrator = checked;
                newPerms.changeScreenSettings = checked;
                newPerms.editTemplates = checked;
            } else {
                // If any other permission is enabled, disable administrator
                if (checked && prev.administrator) {
                    newPerms.administrator = false;
                }
                newPerms[permission] = checked;
            }

            return newPerms;
        });
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing
                            ? t("users.roles.form.title.edit", { name: role?.name })
                            : t("users.roles.form.title.create")}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                        label={t("users.roles.form.name.label")}
                        registration={register("name")}
                        error={errors.name?.message}
                        touched={touchedFields.name}
                        placeholder={t("users.roles.form.name.placeholder")}
                        required
                    />

                    <div className="space-y-4">
                        <h3>{t("users.roles.form.permissions.title")}</h3>

                        <Card className="space-y-4 p-4">
                            {/* Administrator */}
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label inputId="administrator">
                                        {t("users.roles.form.permissions.administrator.label")}
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                        {t(
                                            "users.roles.form.permissions.administrator.description",
                                        )}
                                    </p>
                                </div>
                                <Switch
                                    id="administrator"
                                    checked={simplePermissions.administrator}
                                    onCheckedChange={(checked) =>
                                        handlePermissionChange("administrator", checked)
                                    }
                                />
                            </div>

                            {/* Change Screen Settings */}
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label inputId="changeScreenSettings" bold>
                                        {t(
                                            "users.roles.form.permissions.changeScreenSettings.label",
                                        )}
                                    </Label>
                                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                                        {t(
                                            "users.roles.form.permissions.changeScreenSettings.description",
                                        )}
                                    </p>
                                </div>
                                <Switch
                                    id="changeScreenSettings"
                                    checked={simplePermissions.changeScreenSettings}
                                    onCheckedChange={(checked) =>
                                        handlePermissionChange("changeScreenSettings", checked)
                                    }
                                    disabled={simplePermissions.administrator}
                                />
                            </div>

                            {/* Edit Templates */}
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label inputId="editTemplates">
                                        {t("users.roles.form.permissions.editTemplates.label")}
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                        {t(
                                            "users.roles.form.permissions.editTemplates.description",
                                        )}
                                    </p>
                                </div>
                                <Switch
                                    id="editTemplates"
                                    checked={simplePermissions.editTemplates}
                                    onCheckedChange={(checked) =>
                                        handlePermissionChange("editTemplates", checked)
                                    }
                                    disabled={simplePermissions.administrator}
                                />
                            </div>
                        </Card>
                    </div>

                    <div className="flex justify-end space-x-2 pt-6">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            {t("general.cancel")}
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting
                                ? isEditing
                                    ? t("users.roles.form.submit.updating")
                                    : t("users.roles.form.submit.creating")
                                : isEditing
                                  ? t("users.roles.form.submit.update")
                                  : t("users.roles.form.submit.create")}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
